import { Box, Typography, Stack, Button, Divider, Autocomplete, TextField, Chip, Paper, IconButton } from '@mui/material';
import PlusIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useState, useEffect } from 'react';
import DialogCadastrarAtributo from './DialogCadastrarAtributo';
import { useMutation } from 'react-query';
import axiosClient from '../../../../../../lib/axios';
import { enqueueSnackbar } from 'notistack';

interface IHierarchyItem {
  id: number;
  nome: string;
  atributos: string[];
}

interface ISubCategoria {
  id: number;
  categoriaId: number;
  nome: string;
  atributos: Atributo[];
  ordem: string[];
  subcategorias?: ISubCategoria[];
}

interface Atributo {
  id: number;
  categoria_id: number;
  nome: string;
  descricao: string;
  compoe_nome: boolean;
  obrigatorio: boolean;
  tipo: string;
  ordem_nome?: number;
}

interface ICategoria {
  id: number;
  nome: string;
  atributos: Atributo[];
  hierarchy?: IHierarchyItem[];
  ordem: string[];
  subcategorias: ISubCategoria[];
}

interface IDetalheCategoria {
  onSuccess: () => void;
  selectedData: ICategoria | null;
  editing: number | null;
  handleEdit: (id: number) => void;
  handleSave: (id: number) => void;
  handleCancel: () => void;
  handleRemoveAtributo: (id: number, index: number) => void;
  handleRemoveOrdem: (id: number, index: number) => void;
  editedAtributos: { [key: number]: string[] };
  editedOrdem: { [key: number]: string[] };
  setEditedAtributos: (value: { [key: number]: string[] }) => void;
  setEditedOrdem: (value: { [key: number]: string[] }) => void;
}

export default function DetalheCategoria({
  selectedData,
  editing,
  handleRemoveOrdem,
  editedOrdem,
  setEditedOrdem,
  onSuccess,
}: IDetalheCategoria) {
  const [addAtributo, setAddAtributo] = useState<boolean>(false);
  const [ordenedAtributos, setOrdenedAtributos] = useState<Atributo[]>([]);
  const [isOrdering, setIsOrdering] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (selectedData && selectedData.atributos) {
      organizeAtributos(selectedData.atributos);
    }
  }, [selectedData]);

  const organizeAtributos = (atributos: Atributo[]) => {
    const compoeNome = atributos
      .filter(a => a.compoe_nome)
      .sort((a, b) => (a.ordem_nome || 0) - (b.ordem_nome || 0));

    const outros = atributos.filter(a => !a.compoe_nome);

    setOrdenedAtributos([...compoeNome, ...outros]);
    setHasChanges(false);
  };


  const handleDragStart = (index: number) => {
    if (!isOrdering) return;
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    if (!isOrdering) return;
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) return;

    const newOrdered = [...ordenedAtributos];
    const draggedItem = newOrdered[draggedIndex];

    newOrdered.splice(draggedIndex, 1);
    newOrdered.splice(index, 0, draggedItem);

    setOrdenedAtributos(newOrdered);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleStartOrdering = () => {
    setIsOrdering(true);
  };

  const handleCancelOrdering = () => {
    setIsOrdering(false);
    setHasChanges(false);
    if (selectedData && selectedData.atributos) {
      organizeAtributos(selectedData.atributos);
    }
  };

  const handleSaveOrder = () => {
    const atributosQueCompoemNome = ordenedAtributos
      .filter(atributo => atributo.compoe_nome)
      .map((atributo, index) => ({
        id: atributo.id,
        categoria_id: atributo.categoria_id,
        ordem: index + 1,
      }));

    updateAtributoOrder(atributosQueCompoemNome);
  };


  const { mutate: updateAtributoOrder } = useMutation(
    async (categorias: Array<{ id: number; categoria_id: number; ordem: number }>) => {
      const { data } = await axiosClient.post('/estoque/categoria/ordenar', { categorias });
      return data;
    },
    {
      onSuccess: () => {
        enqueueSnackbar('Ordem salva com sucesso!', { variant: 'success' });
        onSuccess();
        setIsOrdering(false);
      },
      onError: () => {
        enqueueSnackbar('Erro ao salvar a ordem', { variant: 'error' });
      }
    }
  );
  const getAtributoLabel = (attr: Atributo) => {
    const labels = [];
    if (attr.compoe_nome) labels.push('Compõe Nome');
    if (attr.obrigatorio) labels.push('Obrigatório');
    if (!attr.compoe_nome && !attr.obrigatorio) labels.push('Opcional');
    return labels.join(' • ');
  };

  const openDialogAddAtributo = () => {
    setAddAtributo(true);
  };

  const closeDialogAddAtrubuto = () => {
    setAddAtributo(false);
    onSuccess();
  };

  const safeHierarchy = selectedData?.hierarchy || (selectedData ? [{
    id: selectedData.id,
    nome: selectedData.nome || 'Sem nome',
    atributos: selectedData.atributos ?? []
  }] : []);
  return (
    <>
      <DialogCadastrarAtributo
        open={addAtributo}
        onClose={closeDialogAddAtrubuto}
        onSuccess={() => {
          closeDialogAddAtrubuto();
        }}
        categoriaId={selectedData?.id ?? 0}
      />
      <Box flex={1} sx={{ backgroundColor: 'background.paper', borderRadius: 2 }}>
        {!selectedData ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography color="error" textAlign="center">
              <b>Nenhum grupo selecionado.</b> <br />
              Selecione um grupo para continuar.
            </Typography>
          </Box>
        ) : (
          <Box p={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">
                {safeHierarchy.map((item, index) => (
                  <span key={item.id}>
                    {item.nome}
                    {index < safeHierarchy.length - 1 && ' > '}
                  </span>
                ))}
              </Typography>
              <Button
                startIcon={<PlusIcon />}
                variant="outlined"
                onClick={openDialogAddAtributo}
              >
                Adicionar
              </Button>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body1" fontWeight="bold">
                Atributos
              </Typography>

              {!isOrdering ? (
                <Button
                  startIcon={<SwapVertIcon />}
                  variant="text"
                  size="small"
                  onClick={handleStartOrdering}
                >
                  Ordenar
                </Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button
                    startIcon={<CloseIcon />}
                    variant="outlined"
                    size="small"
                    onClick={handleCancelOrdering}
                  >
                    Cancelar
                  </Button>
                  <Button
                    startIcon={<SaveIcon />}
                    variant="contained"
                    size="small"
                    onClick={handleSaveOrder}
                    disabled={!hasChanges}
                  >
                    Salvar
                  </Button>
                </Stack>
              )}
            </Stack>

            {/* Lista de atributos */}
            <Box mb={3}>
              <Stack spacing={1}>
                {ordenedAtributos.map((attr, index) => (
                  <Box
                    key={attr.id}
                    draggable={isOrdering && attr.compoe_nome}
                    onDragStart={() => attr.compoe_nome && handleDragStart(index)}
                    onDragOver={(e) => attr.compoe_nome && handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      cursor: isOrdering && attr.compoe_nome ? 'grab' : 'default',
                      opacity: draggedIndex === index ? 0.5 : 1,
                      bgcolor: isOrdering && attr.compoe_nome ? 'action.hover' : 'transparent',
                    }}
                  >

                    {isOrdering && (
                      <DragIndicatorIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    )}
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {attr.nome}
                    </Typography>

                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {getAtributoLabel(attr)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Seção de Ordem */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Ordem do nome
              </Typography>
            </Stack>

            {editing === selectedData.id ? (
              <Box>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={editedOrdem[selectedData.id] || []}
                  onChange={(event, newValue) => {
                    setEditedOrdem({
                      ...editedOrdem,
                      [selectedData.id]: newValue
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Digite um item da ordem e pressione Enter"
                      size="small"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        onDelete={() => handleRemoveOrdem(selectedData.id, index)}
                      />
                    ))
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Digite um item da ordem e pressione Enter para adicionar
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {(selectedData.ordem || []).map((item: string, i: number) => (
                  <Paper
                    key={i}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {item}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}